(() => {
  const truthy = (value) => value === true || String(value).toLowerCase() === 'true';
  const values = (value) => (Array.isArray(value) ? value : [value]).map((item) => String(item || '').trim().toLowerCase());
  const findRule = (rules, country, availability) => rules.find((rule) => (
    values(rule.countryCodes).includes(String(country || '').toLowerCase())
    && values(rule.availability).includes(String(availability || '').trim().toLowerCase())
  ));
  const isPreorder = (rule, availability) => truthy(rule?.preorder) || values(availability).includes('preorder');
  const parseDate = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
  };
  const dateRange = (rule, locale = document.documentElement.lang || 'de') => {
    const start = parseDate(rule?.fixedDate);
    const end = parseDate(rule?.fixedDateEnd);
    if (!start || !end || end < start) return null;
    const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
    const german = locale.toLowerCase().startsWith('de');
    const shortDate = (date) => german
      ? `${date.getUTCDate()}.${date.getUTCMonth() + 1}${sameYear ? '' : `.${date.getUTCFullYear()}`}`
      : new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', ...(!sameYear && { year: 'numeric' }), timeZone: 'UTC' }).format(date);
    return { start: shortDate(start), end: shortDate(end) };
  };
  const duration = (rule) => {
    if (!rule || [rule.minTime, rule.maxTime].some((v) => v === null || v === undefined || String(v).trim() === '')) return null;
    const min = Number(rule.minTime), max = Number(rule.maxTime);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < min) return null;
    return { min, max, weeks: truthy(rule.weeks), range: min === max ? String(min) : `${min}–${max}` };
  };
  const format = (rule, labels, faq = false, locale) => {
    const dates = dateRange(rule, locale);
    if (dates) return (faq ? labels.faqDeliveryDateLabel : labels.deliveryDateLabel)
      .replace('__start__', dates.start).replace('__end__', dates.end);
    if (rule?.fixedDate || rule?.fixedDateEnd) return labels.deliveryNoInfoLabel;
    const time = duration(rule);
    if (!time) return labels.deliveryNoInfoLabel;
    const label = faq
      ? (time.weeks ? labels.faqDeliveryWeeksLabel : labels.faqDeliveryDaysLabel)
      : (time.weeks ? labels.deliveryWeeksLabel : labels.deliveryDaysLabel);
    return label.replace('__range__', time.range);
  };
  const windowFor = (rule, now) => {
    if (dateRange(rule)) return [parseDate(rule.fixedDate), parseDate(rule.fixedDateEnd)];
    if (rule?.fixedDate || rule?.fixedDateEnd) return null;
    const time = duration(rule);
    if (!time) return null;
    const add = (count) => {
      const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      if (time.weeks) date.setUTCDate(date.getUTCDate() + count * 7);
      else for (let remaining = count; remaining > 0;) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (![0, 6].includes(date.getUTCDay())) remaining -= 1;
      }
      return date;
    };
    return [add(time.min), add(time.max)];
  };
  const cartStatus = (items, rules, country, now = new Date()) => {
    // Native bundle wrappers have no delivery availability of their own; use each included product.
    const physicalItems = items.flatMap((item) => item.components?.length ? item.components : [item])
      .filter((item) => item.requiresShipping !== false);
    const matches = physicalItems.map((item) => ({ rule: findRule(rules, country, item.availability), availability: item.availability }));
    const preorder = matches.some(({ rule, availability }) => isPreorder(rule, availability));
    if (!matches.length) return { visible: false, preorder: false, rule: null };
    const windows = matches.map(({ rule }) => windowFor(rule, now));
    if (windows.some((window) => !window)) return { visible: true, preorder, rule: null };
    const durations = matches.map(({ rule }) => duration(rule));
    if (matches.every(({ rule }) => !rule.fixedDate && !rule.fixedDateEnd) && durations.every((time) => time.weeks === durations[0].weeks)) {
      return { visible: true, preorder, rule: { minTime: Math.max(...durations.map((time) => time.min)), maxTime: Math.max(...durations.map((time) => time.max)), weeks: durations[0].weeks } };
    }
    return { visible: true, preorder, rule: {
      fixedDate: new Date(Math.max(...windows.map((window) => window[0].getTime()))).toISOString().slice(0, 10),
      fixedDateEnd: new Date(Math.max(...windows.map((window) => window[1].getTime()))).toISOString().slice(0, 10),
    } };
  };
  window.MarmeladeDelivery = { findRule, isPreorder, parseDate, dateRange, duration, format, cartStatus };
  const registerCart = () => {
    if (customElements.get('marmelade-cart-delivery')) return;
    customElements.define('marmelade-cart-delivery', class extends HTMLElement {
      connectedCallback() {
        const data = JSON.parse(this.querySelector('[data-cart-delivery-json]').textContent);
        const status = cartStatus(data.items, data.rules, this.dataset.countryCode);
        this.hidden = !status.visible;
        this.classList.toggle('is-preorder', status.preorder);
        const text = format(status.rule, this.dataset);
        this.querySelector('[data-cart-delivery-text]').textContent = status.preorder ? `${this.dataset.preorderLabel} · ${text}` : text;
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', registerCart, { once: true });
  else registerCart();
})();
